import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, update } from '@/services/api/resources/category';
import { versioned } from '@/services/api';
import { toastAndRedirect } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { useCategoryForm } from './useCategoryForm';
import { useCategoryRecord } from './useCategoryRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export interface CategoryEditPageProps {
  id: number;
}

export const CategoryEditPage = defineComponent<CategoryEditPageProps>({
  name: 'CategoryEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('analytics')) {
      return renderResourceTeaserPage(
        t('common.categories'),
        t('category.teaser'),
        t('common.teaser'),
        'categoryIndex',
      );
    }

    if (!hasPermission(Permissions.MANAGE_CATEGORIES)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';

    // create empty record
    const record = useCategoryRecord();

    // get record
    const getRecord = useTask(
      () => get(props.id),
      ({ data, version }) => {
        record.value = data.data;
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => toastAndRedirect(t('category.updated'), 'administration', { [queryTabName]: AdministrationTabNames.CATEGORY }),
    );

    // form
    const { formFields, handleSubmit } = useCategoryForm(record, sendRecord);

    return function render() {
      return container(
        [
          pageTitle(t('category.edit')),
          box([loader('form', getRecord, [
            ...formFields(),
            loadingButton(
              t('category.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
